/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';


import { GroupUI } from '../models/group-ui';
import { User } from '../models/user';
import { ObjectInstance } from '../models/object-instance';
import { AuthenticationService } from '../services/authentication.service';
import { ImageService } from '../services/image.service';
import { UserService } from '../services/user.service';
import { UserDialogService } from '../services/user-dialog.service';
import { UserGroupService } from "../services/user-group.service";
import { DropDownItem } from "../models/dropdown-item";
import { IconUploadConfirmDialogService } from 'app/services/icon-upload-confirm-dialog-service.service';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare var $: any;

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html'
})
export class UserDialogComponent implements OnInit {

  /** Holds whether only the Password should be editable or not.  Changed depending on what is being done (Edit Profile / Edit User / Add User) */
  onlyPassword: boolean = false;

  //User being edited, along with the Tag selected...
  mUser: User = new User();
  mPerson: ObjectInstance = null;

  // Person originally assigned to the User... When updating the Assigned Person,
  // the previously assigned Person must be removed first so we need to remember it
  mOldPerson: ObjectInstance = null;

  //User Name displayed in the title bar when editing a User (This can't be tied to the model's firstName + lastName since it would update if those are changed and that's not the behavior I'm seeing in the old UI)
  mUserName = "";

  //Available People that the User can assign to the User
  mAvailablePeople: ObjectInstance[] = [];

  //Available Groups that the User can assign the User
  mAvailableGroups: GroupUI[] = [];

  //Flags used when retrieving People/Groups to prevent multiple of the same instances from being populated on screen.
  mPeopleRetrieved = true;
  mGroupsRetrieved = true;

  @ViewChild('userImage')
  mUserImageFileInput: ElementRef;

  @ViewChild('userIcon')
  mUserIconFileInput: ElementRef;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Holds the last request sent for checking whether a Password is valid or not.  Used to determine if the response from the Server should be used since it is very hard to cancel a Promise in angular
  mCheckPasswordPromise: Promise<any>;

  mIsPasswordValid = false;
  mPasswordErrorMessage = "Password is required.";

  //Flag used to indicate what type of Dialog is being used (New = 0, Edit = 1, Profile = 2)
  mDialogType = 0;

  // Variables for the multi-selection role selector
  roleDropDownList = [];
  roleSelectedItems = [];
  roleDropDownSettings = {
    singleSelection: false,
    text: "Roles:",
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    enableSearchFilter: true,
    classes:'myclass custom-class',
    maxHeight: 150,
    position: "top"
  };
  roleDropDownSettingsDisabled = {
    singleSelection: false,
    text: "Roles:",
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    enableSearchFilter: true,
    classes:'myclass custom-class',
    maxHeight: 150,
    position: "top",
    disabled: true
  };

  // Variables for the multi-selection people selector
  peopleDropDownList = [];
  peopleSelectedItems = [];
  peopleDropDownSettings = {
    singleSelection: false,
    text: "People:",
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    enableSearchFilter: true,
    classes:'myclass custom-class',
    maxHeight: 150,
    noDataLabel:'No People available',
    position:"top"
  };


  constructor(
    private mUserService: UserService,
    private mImageService: ImageService,
    private mUserDialogService: UserDialogService,
    private userGroupService: UserGroupService,
    private mAuthenticationService: AuthenticationService,
    private mIconUploadConfirmDialogService: IconUploadConfirmDialogService
  ) { }


  ngOnInit(): void {
    this.mUserDialogService.getNewUserListener().subscribe(value => {
      this.showAddUserDialog();
    });

    this.mUserDialogService.getEditUsersListeners().subscribe(value => {
      this.showEditUserDialog(value.user, value.tag);
    });

    this.mUserDialogService.getProfileListener().subscribe(value => {
      this.showEditProfileDialog(value.user, value.tag);
    });
  }



  public imageSource(): string {
    return this.mImageService.getImageSourceForUser(this.mUser);
  }



  showAddUserDialog(): void {
    this.mDialogType = 0;
    this.onlyPassword = false;

    //Set the original Assigned Person for the User (empty since this is a new User)
    this.mOldPerson = null;

    //Create the User that will be shown/displayed in the UI and will be sent to the Server
    const newUser = new User();
    //These values cannot be null (Causing some issues on the Server, should probably be handled there) NOTE: Remove this in the future
    newUser.login = "";
    newUser.password = "";
    newUser.lastName = "";
    newUser.groups = [];
    this.mUser = newUser;

    //New User is currently not assigned a Person, so set to null
    this.mPerson = null;

    //Retrieve the available People to assign the User
    if (this.mPeopleRetrieved) {
      this.mPeopleRetrieved = false;
      this.refreshPeople();
    }

    //Retrieve the available Groups to assign the User
    if (this.mGroupsRetrieved) {
      this.mGroupsRetrieved = false;
      this.refreshGroups();
    }

    //Reset the Password Validation Fields...
    this.mIsPasswordValid = false;
    this.mPasswordErrorMessage = "Password is required.";

    //Show the Dialog
    $("#userModal").modal("show");
  }

  showEditUserDialog(user: User, person: ObjectInstance): void {
    console.log("EDIT USER DIALOG");
    this.mDialogType = 1;
    this.onlyPassword = false;

    //Set the User Name displayed in the title
    this.mUserName = user.firstName + " " + user.lastName;

    //Set the User/Tag that is being edited
    this.mUser = JSON.parse(JSON.stringify(user)) as User;
    this.mUser.password = "";
    this.mPerson = person;
    this.mOldPerson = person;

    //Retrieve the available People to assign the User (This also selects the one passed in/set above)
    if (this.mPeopleRetrieved) {
      this.mPeopleRetrieved = false;
      this.refreshPeople();
    }

    //Retrieve the available Groups to assign the User (This also selects the ones that are currently assigned to the User being edited...)
    if (this.mGroupsRetrieved) {
      this.mGroupsRetrieved = false;
      this.refreshGroups();
    }

    // Create the role drop down list
    for(const group of this.mAvailableGroups) {
      this.roleDropDownList.push(new DropDownItem(group.mGroup.id, group.mGroup.name));
      if (group.mSelected) {
        this.roleSelectedItems.push();
      }
    }

    //Reset the Password Validation Fields...
    this.mIsPasswordValid = true;
    this.mPasswordErrorMessage = "Password will not be changed.";

    //Show the Dialog
    $("#userModal").modal("show");
  }

  showEditProfileDialog(user: User, person: ObjectInstance): void {
    this.mDialogType = 2;
    this.onlyPassword = true;

    //Set the User Name displayed in the title
    this.mUserName = user.firstName + " " + user.lastName;

    //Set the User/Tag that is being edited
    this.mUser = JSON.parse(JSON.stringify(user)) as User;
    this.mUser.password = "";
    this.mPerson = person;
    this.mOldPerson = person;

    //Retrieve the available Tags to assign the User (This also selects the one passed in/set above)
    if (this.mPeopleRetrieved) {
      this.mPeopleRetrieved = false;
      this.refreshPeople();
    }

    //Retrieve the available Groups to assign the User (This also selects the ones that are currently assigned to the User being edited...)
    if (this.mGroupsRetrieved) {
      this.mGroupsRetrieved = false;
      this.refreshGroups();
    }

    //Reset the Password Validation Fields...
    this.mIsPasswordValid = true;
    this.mPasswordErrorMessage = "Password will not be changed.";

    //Show the Dialog
    $("#userModal").modal("show");
  }

  hideDialog(): void {
    console.log("UserDialogComponent.hideDialog()");

    //Hide the Dialog and reset the User being edited/created...
    $("#userModal").modal("hide");
    this.mUser = new User();
  }

  refreshPeople(): void {
    this.mAvailablePeople = [];
    this.mUserService.getAllUnassignedPeople()
      .then(
        people => {

          if (people != null) {
            for (const person of people) {
              this.mAvailablePeople.push(person);
            }

            // Add all the emAssets assigned to this user
            if (this.mPerson != null) {
              this.mAvailablePeople.push(this.mPerson);
            }

          }

          if (this.mUser && this.mUser.id) {
            this.mUserService.getPersonForUser(this.mUser.id).then(
              response => {
                if (response == null) {
                  this.mPerson = null;
                  console.log("Person assigned to User " + this.mUser.id + " is NULL");

                } else {
                  this.mPerson = response;
                  this.mOldPerson = response;
                  console.log("Person assigned to User " + this.mUser.id + " is " + this.mPerson.name);
                  this.mAvailablePeople.push(this.mPerson);
                }

                this.mPeopleRetrieved = true;

              },
              error => {
                alert("Error obtaining Person for user: " + error);
              }
            )
          } else {

            this.mPerson = null;
            this.mPeopleRetrieved = true;
          }

        },
        error => {
          if (error != null) {
            alert("Error obtaining Tags: " + error);
            this.mPeopleRetrieved = true;
          }
        }
      );


  }

  refreshGroups(): void {
    this.mAvailableGroups = [];
    this.userGroupService
      .getAllUserGroups()
      .then(
        groups => {
          if (groups != null) {
            for (const group of groups) {
              const groupUi = new GroupUI();
              groupUi.mGroup = group;

              //If the User has groups that are already part of it, check to see if this is one of them and select it if needed
              if (this.mUser != null && this.mUser.groups != null) {
                for (const userGroup of this.mUser.groups) {
                  if (userGroup.id === group.id) {
                    groupUi.mSelected = true;
                    continue;
                  }
                }
              }

              this.mAvailableGroups.push(groupUi);
            }
          }
        this.mGroupsRetrieved = true;

          // Create the role drop down list
          this.roleDropDownList = [];
          for(const group of this.mAvailableGroups) {
            this.roleDropDownList.push(new DropDownItem(group.mGroup.id, group.mGroup.name));
          }

          // Create the list of roles that this user has
          this.roleSelectedItems = [];
          for(const group of this.mUser.groups) {
            this.roleSelectedItems.push(new DropDownItem(group.id, group.name));
          }

        },
        error => {
          if (error != null) {
            alert("Error obtaining User Groups: " + error);
            this.mGroupsRetrieved = true;
          }
        }
      );
  }

  isEditDialog(): boolean {
    return this.mDialogType === 1;
  }

  isNewDialog(): boolean {
    return this.mDialogType === 0;
  }

  isProfileDialog(): boolean {
    return this.mDialogType === 2;
  }

  changeImageClicked(): void {
    this.mUserImageFileInput.nativeElement.click();
  }

  onImageFileChange(event: any): void {
    if (event.target.files.length === 1) {
      this.submitImage(event.target.files[0]);
    }
  }

  resetImage(): void {
    if (this.mUser != null) {
      this.mUser.image = null;
    }
  }

  submitImage(file: any): void {
    const formData = new FormData();
    formData.append("file", file);
    this.mImageService
      .uploadImage(formData)
      .then(
        image => {
          if (this.mUser != null) {
            this.mUser.image = image;
          }
          this.mUserImageFileInput.nativeElement.value = "";
        },
        error => {
          if (error != null) {
            alert("Error submitting Image: " + error);
            this.mUserImageFileInput.nativeElement.value = "";
          }
        }
      );
  }

  changeIconClicked(): void {
    this.mUserIconFileInput.nativeElement.click();
  }

  onIconFileChange(event: any): void {
    if (event.target.files.length === 1) {
      this.submitIcon(event.target.files[0]);
    }
  }

  resetIcon(): void {
    if (this.mUser != null) {
      this.mUser.icon = null;
    }
  }

  submitIcon(file: any): void {
    //KHT 08/24/2021 - added IconUploadConfirmDialog to confirm icon resize on upload
    this.mIconUploadConfirmDialogService.show().then((confirmed) => {
      if (confirmed) {
        console.log("Icon Upload Proceeding...");
        const formData = new FormData();
        formData.append("file", file);
        formData.append("isIcon", "true"); //KHT 08/24/2021 - backend checks for 'isIcon' in the formData, and resizes the icon if it is too large
        this.mImageService
          .uploadImage(formData)
          .then(
            image => {
              if (this.mUser != null) {
                this.mUser.icon = image;
              }
              this.mUserIconFileInput.nativeElement.value = "";
            },
            error => {
              if (error != null) {
                alert("Error submitting Icon: " + error);
                this.mUserIconFileInput.nativeElement.value = "";
              }
            }
          );
      }
      else{
        console.log("Icon Upload Halted!");
      }
    }).catch(() => {
      console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)')
    });
  }





  checkPassword(): void {
    if (this.isEditDialog() && this.mUser.password.length === 0) {
      this.mCheckPasswordPromise = null;
      this.mIsPasswordValid = true;
      this.mPasswordErrorMessage = "Password will not be changed";
    } else {
      const checkPasswordPromise = this.mUserService
        .validatePassword(this.mUser.id, this.mUser.password)
        .then(
        result => {
          if (this.mCheckPasswordPromise === checkPasswordPromise) {
            this.mCheckPasswordPromise = null;
            this.mPasswordErrorMessage = null;
            this.mIsPasswordValid = true;
          }
        },
        error => {
          if (error != null) {
            if (this.mCheckPasswordPromise === checkPasswordPromise) {
              this.mCheckPasswordPromise = null;
              this.mIsPasswordValid = false;
              this.mPasswordErrorMessage = error._body;
            }
          }
        }
      );
      this.mCheckPasswordPromise = checkPasswordPromise;
    }
  }





  async addUser(): Promise<void> {
    //Set the Groups...
    this.mUser.groups = [];
    if (this.mAvailableGroups != null && this.mAvailableGroups.length > 0) {
      for (const group of this.mAvailableGroups) {
        if (group.mSelected) {
          this.mUser.groups.push(group.mGroup);
        }
      }
    }

    try {
      //Create the User
      const createdUser = await this.mUserService.createUser(this.mUser);

      try {
        // Assign person to user (if necessary)
        if (this.mPerson) {
          await this.mUserService.assignUserToPerson(createdUser.id, this.mPerson.id);
        }
      } catch (error_assignUser) {
        console.error(`User saved but failed to assign Tag to User. Error: ${error_assignUser._body}...`);
        this.handleError(error_assignUser);
      }

      this.fireRefreshUsers();
      this.hideDialog();

    } catch (error_createUser) {
      console.error(`AddUser failed: ${error_createUser._body}`);
      this.handleError(error_createUser);
    }

  }

  async updateUser(): Promise<void> {
    console.log("updateUser");
    //If no password was entered, the User is not updating the password. Set it to null so the Server ignores it
    if (this.mUser.password === null || this.mUser.password.length === 0) {
      this.mUser.password = null;
    }

    //Set the Groups...
    this.mUser.groups = [];
    if (this.mAvailableGroups != null && this.mAvailableGroups.length > 0) {
      for (const group of this.mAvailableGroups) {
        if (group.mSelected) {
          this.mUser.groups.push(group.mGroup);
        }
      }
    }

    //Update the User
    const updatedUser = await this.mUserService.updateUser(this.mUser);
    if (updatedUser.id === this.mAuthenticationService.getUser().id) {
      try {
        this.mAuthenticationService.updateUser(updatedUser);
      } catch (error) {
        console.error(`Unable to update User. Error: ${error._body}`);
        this.handleError(error);
      }
    }

    // Unassign a person from the User if necessary
    if ((this.mOldPerson && this.mPerson && this.mOldPerson.id != this.mPerson.id) ||
        (this.mOldPerson && this.mPerson == null)) {
      try {
        console.log("Unassign User " + this.mUser.id + " From Person: " + this.mOldPerson.id);
        //Need to un-assign the old Person from the User since the Assigned Person is changing...
        await this.mUserService.unassignPersonFromUser(this.mUser.id);
      } catch (error) {
        console.error(`User updated but failed to unassign Person ${this.mOldPerson.name} from User. Error: ${error._body}`);
        this.handleError(error);
      }
    }

    // Assign a person to this User if necessary
    if ((this.mPerson && this.mOldPerson == null) ||
        (this.mOldPerson && this.mPerson && this.mOldPerson.id != this.mPerson.id)) {
      try {
        console.log("Assign User " + this.mUser.id + " To Person: " + this.mPerson.id);
        await this.mUserService.assignUserToPerson(this.mUser.id, this.mPerson.id);
      } catch (error) {
        console.error(`User updated but failed to assign Person ${this.mOldPerson.name} to User. Error: ${error._body}`);
        this.handleError(error);
      }
    }

    this.fireRefreshUsers();
    this.hideDialog();

  }

  fireRefreshUsers(): void {
    this.mUserDialogService.refreshUsers();
  }

  selectRoleItem(event: any): void {
    for(const groupUI of this.mAvailableGroups) {
      if (groupUI.mGroup.id == event.id) {
        groupUI.mSelected = true;
        break;
      }
    }
  }

  deselectRoleItem(event: any): void {
    for(const groupUI of this.mAvailableGroups) {
      if (groupUI.mGroup.id == event.id) {
        groupUI.mSelected = false;
        break;
      }
    }
  }

  selectAllRoleItems(): void {
    for(const groupUI of this.mAvailableGroups) {
      groupUI.mSelected = true;
    }
  }

  deselectAllRoleItems(): void {
    for(const groupUI of this.mAvailableGroups) {
      groupUI.mSelected = false;
    }
  }

  personSelected(personId: number): void {
    if (personId == -1) {
      this.mPerson = null;
      console.log("No person selected");
    } else {
      for (const availPerson of this.mAvailablePeople) {
        if (availPerson.id == personId) {
          this.mPerson = availPerson;
        }
      }
      console.log("Person selected: " + this.mPerson.name);
    }
  }

/*
  selectTagItem(event: any): void {
    console.log("Adding Tag item: " + event.id);
    for(const peopleUI of this.mAvailablePeople) {
      if (peopleUI.person.id == event.id) {
        peopleUI.selected = true;
      }
    }
    //this.setPeopleToAvailablePeopleSelected();
  }

  deselectTagItem(event: any): void {
    for(const peopleUI of this.mAvailablePeople) {
      if (peopleUI.person.id == event.id) {
        peopleUI.selected = false;
      }
    }
    //this.setPeopleToAvailablePeopleSelected();
  }
*/

  /*
  selectAllTagItems(): void {
    for(const peopleUI of this.mAvailablePeople) {
      peopleUI.selected = true;
    }
    this.setPeopleToAvailablePeopleSelected();
  }

  deselectAllTagItems(): void {
    for(const peopleUI of this.mAvailablePeople) {
      peopleUI.selected = false;
    }
    this.setPeopleToAvailablePeopleSelected();
  }
*/



  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error: ${error}`)
    console.error(`Error JSON: ${JSON.stringify(error)}`)
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
